import React from 'react';
import DataTable, { Column } from '@/components/common/DataTable';
import Pagination from '@/components/common/Pagination';
import { DateRangePicker } from '@/components/common/DateRangePicker';
import { useEventListing } from './hooks/useEventListing';
import dayjs from 'dayjs';
import { ToggleButton } from '@/components/common/Toggle';
import CustomSelect from '@/components/common/CustomSelect';
import ResultTabs from './components/ResultTabs';
import ResultModal from './components/ResultModal';
import CancelConfirmModal from './components/CancelConfirmModal';
import { PendingMarketItem } from '@/types/pendingMarket';
import DashboardCard from './components/DashboardCard';
import RollbackConfirmModal from './components/RollbackConfirmModal';

const EventListingPage: React.FC = () => {
  const BET_CARDS_CONFIG = [
    {
      key: 'totalBets',
      label: 'Total Bets',
      className: 'border-indigo-600 bg-indigo-100 text-indigo-600',
    },
    {
      key: 'settledBets',
      label: 'Settled Bets',
      className: 'border-green-700 bg-green-100 text-green-700',
    },
    {
      key: 'unsettledBets',
      label: 'Unsettled Bets',
      className: 'border-amber-600 bg-amber-100 text-amber-600',
    },
    {
      key: 'rollbackBets',
      label: 'Rollback Bets',
      className: 'border-red-700 bg-red-100 text-red-700',
    },
  ];

  const [searchText, setSearchText] = React.useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
  const [cancelTarget, setCancelTarget] = React.useState<PendingMarketItem | null>(null);
  const {
    items,
    totalItems,
    isTableLoading,
    page,
    limit,
    setPage,
    setLimit,
    dateRange,
    setDateRange,
    debouncedSearch,
    open,
    setOpen,
    selectedEventName,
    runners,
    marketType,
    marketName,
    handleOpenResult,
    handleResultAction,
    betResultLoading,

    isPollingEnabled,
    setIsPollingEnabled,
    // runnersLoading,

    // FILTERS
    sport,
    // competitionId,
    // eventId,
    sportOptions,
    // competitionOptions,
    // eventOptions,
    handleSportChange,
    // handleCompetitionChange,
    // setEventId,
    // competitionLoading,
    // eventsLoading,
    webhookName,
    webhookNameOptions,
    handleWebhookNameChange,

    betCounts,
    activeResultTab,
    handleTabChange,
    rollbackConfirmOpen,
    setRollbackConfirmOpen,
    lastResult,
    marketNameLabel,
  } = useEventListing();

  const columns: Column[] = [
    { key: 'eventId', label: 'Event ID', className: 'font-semibold' },
    {
      key: 'placedAt',
      label: 'Placed At',
      render: (value) => <div>{value ? dayjs(value).format('DD/MM/YYYY, h:mm A') : '-'}</div>,
    },
    { key: 'selection', label: 'Selection' },
    ...(activeResultTab === 'rollback'
      ? [
          {
            key: 'resultToShow',
            label: 'Last Result',
            render: (value: string) => (value == '-999' ? 'Cancelled' : value || '-'),
          },
        ]
      : []),
    {
      key: 'marketName',
      label: 'Market Name',
      render: (value: string) => <div>{value || '-'}</div>,
    },
    { key: 'eventName', label: 'Events' },
    {
      key: 'result',
      label: 'Result',
      render: (_, row) => (
        <button
          onClick={() => handleOpenResult(row)}
          className={`text-white px-4 py-1 rounded-md bg-green-700 hover:bg-green-900 
           ${activeResultTab === 'rollback' && 'bg-yellow-500 hover:bg-yellow-600'}`}
        >
          {activeResultTab === 'rollback' ? 'Rollback' : 'Result'}
        </button>
      ),
    },
    { key: 'marketType', label: 'Market Type' },
    ...(activeResultTab !== 'rollback'
      ? [
          {
            key: 'cancel',
            label: 'Cancel',
            render: (_: any, row: any) => (
              <button
                onClick={() => {
                  setCancelTarget(row);
                  setIsCancelModalOpen(true);
                }}
                className="text-white px-4 py-1 rounded-md bg-red-600 hover:bg-red-700"
              >
                Cancel
              </button>
            ),
          },
        ]
      : []),
  ];
  const currentAction = activeResultTab === 'pending' ? 'declare' : 'rollback';
  return (
    <div className="py-4 px-2 md:px-4">
      {/* Data  */}
      <div className="flex gap-4 w-full items-center justify-between flex-wrap mb-3">
        <div className="flex gap-4 flex-1">
          {BET_CARDS_CONFIG.map((card) => (
            <DashboardCard
              key={card.key}
              title={card.label}
              count={betCounts[card.key as keyof typeof betCounts] ?? 0}
              className={card.className}
            />
          ))}
        </div>
      </div>
      {/* HEADER */}
      <div className="flex justify-between items-center gap-x-8 mb-3">
        <div className="flex justify-start items-center gap-x-6 gap-y-3">
          <div>
            <h2 className="text-sm px-0.5 mb-1">Search</h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Search Here ..."
                value={searchText}
                className="border border-primary-gray-border px-3 py-2 pr-8 rounded-md text-sm w-full"
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchText(value);
                  debouncedSearch(value);
                }}
              />

              {searchText && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchText('');
                    debouncedSearch('');
                    setPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="min-w-[200px]">
            <h2 className="text-sm px-0.5 mb-1">Platforms</h2>
            <CustomSelect
              value={webhookName}
              options={webhookNameOptions}
              onChange={handleWebhookNameChange}
              placeholder="Select Platform"
            />
          </div>
          {/* SPORT FILTER */}
          <div className="min-w-[200px]">
            <h2 className="text-sm px-0.5 mb-1">Sports</h2>
            <CustomSelect
              value={sport}
              options={sportOptions}
              onChange={handleSportChange}
              placeholder="Select Sport"
            />
          </div>
        </div>
        <div className="">
          <DateRangePicker
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-x-4 mb-3">
        <ResultTabs value={activeResultTab} onChange={handleTabChange} />
        <div className="shrink-0 self-center flex justify-end">
          <ToggleButton
            enabled={isPollingEnabled}
            onToggle={() => setIsPollingEnabled((prev) => !prev)}
            label="Auto-refresh"
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="p-2 bg-white rounded-lg">
        <DataTable columns={columns} data={items} isLoading={isTableLoading} />

        <Pagination
          totalItems={totalItems}
          currentPage={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
        />
      </div>

      {/* RESULT MODAL */}
      <ResultModal
        open={open}
        onClose={() => setOpen(false)}
        eventName={selectedEventName}
        marketType={marketType}
        marketName={marketName}
        runners={runners}
        isLoading={betResultLoading}
        onSave={(data, runs) => handleResultAction(currentAction, data, runs)}
      />

      <CancelConfirmModal
        open={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setCancelTarget(null);
        }}
        eventName={cancelTarget?.eventName}
        marketName={cancelTarget?.marketName}
        isLoading={betResultLoading}
        onConfirm={async () => {
          if (!cancelTarget) return;
          const ok = await handleResultAction(currentAction, {}, '', {
            isCancel: true,
            eventDetails: cancelTarget,
          });
          if (ok) {
            setIsCancelModalOpen(false);
            setCancelTarget(null);
          }
        }}
      />
      <RollbackConfirmModal
        open={rollbackConfirmOpen}
        onClose={() => setRollbackConfirmOpen(false)}
        eventName={selectedEventName}
        marketName={marketNameLabel}
        lastResult={lastResult}
        isLoading={betResultLoading}
        onConfirm={async () => {
          const ok = await handleResultAction('rollback', {}, '');
          if (ok) {
            setRollbackConfirmOpen(false);
          }
        }}
      />
    </div>
  );
};

export default EventListingPage;
